import { IAuthorizedUser, TJwtPayload } from 'auth/models'

/**
 * Normalizes text by ensuring proper UTF-8 encoding and cleaning up common encoding issues
 * This helps with character display issues in Flutter across different languages
 * @param text - The text to normalize
 * @returns Normalized text with proper UTF-8 encoding
 */
export const normalizeText = (text: string | undefined): string => {
  if (!text) return ''

  try {
    // Check if text contains common encoding artifacts that need fixing
    const hasEncodingIssues = /â€|â€™|â€œ|â€|â€"/.test(text)

    if (!hasEncodingIssues) {
      // Text seems fine, just normalize Unicode characters and return
      const result = text.normalize('NFC').trim()
      return result
    }

    // Text has encoding issues, try to fix them
    let normalized = text

    // First, try to decode and re-encode to ensure proper UTF-8
    try {
      const decoded = decodeURIComponent(encodeURIComponent(text))
      normalized = decoded
    } catch (decodeError) {
      // If decode fails, continue with original text
      console.warn('Failed to decode text, using original:', decodeError)
    }

    // Normalize Unicode characters (NFD -> NFC)
    normalized = normalized.normalize('NFC')

    // Clean up only the most common encoding artifacts that affect readability
    // These are safe replacements that work across languages
    normalized = normalized
      .replace(/â€™/g, "'") // Replace smart quotes
      .replace(/â€œ/g, '"') // Replace smart quotes
      .replace(/â€/g, '"') // Replace smart quotes
      .replace(/â€"/g, '–') // Replace en dash
      .replace(/â€"/g, '—') // Replace em dash
      .replace(/â€¦/g, '…') // Replace ellipsis
      .replace(/â€¢/g, '•') // Replace bullet
      .trim()

    // // Normalize Unicode characters (NFD -> NFC) after cleaning up artifacts
    // normalized = normalized.normalize('NFC')

    return normalized
  } catch (error) {
    console.warn('Error normalizing text:', error, 'Original text:', text)
    // Return original text if normalization fails
    return text
  }
}

export const fromPayloadToUserMapper = (
  payload: TJwtPayload<IAuthorizedUser>,
): IAuthorizedUser => ({
  client_id: payload.client_id,
  userId: payload.userId,
  clientId: payload.clientId,
  applicationId: payload.applicationId,
  siteId: payload.siteId,
  displayName: normalizeText(payload.displayName),
  position: normalizeText(payload.position),
  isMasterUser: payload.isMasterUser,
  language: normalizeText(payload.language),
  dateFormat: normalizeText(payload.dateFormat),
  numberFormat: normalizeText(payload.numberFormat),
  measurementSystem: normalizeText(payload.measurementSystem),
  permissions: payload.permissions,
  scope: payload.scope,
})

export const BASIC_GUID = '00000000-0000-0000-0000-000000000000'

// TODO: Remove when it will be possible
export const APP_CONNECTOR_ID = 'b73195dd-c9b5-49eb-a9a2-f424f82353c2'

export const createCookieWithTokenAndOriginUrl = (token: string) => {
  let path = window.location.hostname
  const origin = window.location.origin

  if (path !== 'localhost') {
    const pathArray = path.split('.')
    path = `.${pathArray[pathArray.length - 2]}.${pathArray[pathArray.length - 1]}`
  }

  document.cookie = `auth=${token}; path=/; domain=${path}; Secure; SameSite=None;`
  document.cookie = `origin=${origin}; path=/; domain=${path}; Secure; SameSite=None;`
}
